import { useEffect, useMemo, useState } from 'react'
import { USER_STATUS } from '../../domain/domainCodes'
import {
  CircleCheck,
  CircleX,
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  Fish,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react'
import Button from '../../components/Button'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import IconButton from '../../components/IconButton'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import { exportData } from './userDetailsService'
import { strings as S } from './strings'
import './userDetails.css'
import { serverDetail } from '../../lib/i18n'
import { displayDate } from '../../lib/dates'

const asCell = (iso) => displayDate(iso)
const asDay = (iso) => (iso ? displayDate(iso.slice(0, 10)) : '')

/**
 * The detail tabs that are plain tables. Each one keeps the legacy column order,
 * headers and renderers; only البرامج التدريبية and النقاط need their own file.
 *
 * The exports strip is the legacy trio minus `تصدير إلى وورد`, which carries
 * `style="display:none"` in all five places it appears.
 */

/**
 * The two export buttons under an exportable table.
 *
 * They had no handler at all, while `exportData` was written and called by nothing.
 *
 * `tableType` is the `ExportTableType` enum NAME and the endpoint answers 400 to anything
 * it does not recognise, so it is passed in per tab rather than derived from a title.
 */
function Exports({ userId, tableType }) {
  const toast = useToast()
  const [busy, setBusy] = useState('')

  const run = async (format) => {
    setBusy(format)
    const outcome = await exportData({ userId, tableType, format })
    setBusy('')
    // A download reports only its failures — the file arriving IS the success.
    if (!outcome.ok) toast.error(serverDetail(outcome.message) || S.exportFailed)
  }

  return (
    <>
      <Button variant="ghost" disabled={!!busy} onClick={() => run('excel')}>
        <FileSpreadsheet size={16} strokeWidth={1.75} />
        {S.exportExcel}
      </Button>
      <Button variant="ghost" disabled={!!busy} onClick={() => run('pdf')}>
        <FileText size={16} strokeWidth={1.75} />
        {S.exportPdf}
      </Button>
    </>
  )
}

/** Loads once per user and renders a table; `columns` is memoised by the caller. */
/**
 * @param {number} [reloadKey] bump it to re-read. The exam-tries table is the one that
 *        changes underneath itself — a reset gives the user another attempt, and the
 *        remaining count is a column on screen.
 */
function TableTab({ userId, load, columns, title, exportType, getRowId, reloadKey = 0 }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return undefined

    let cancelled = false
    setLoading(true)
    load(userId)
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setLoading(false)
      })
      .catch(() => {
        /* The page toasts the reason; the table stops claiming to be loading rather than
           spinning forever on a tab the licence may not even cover. */
        if (cancelled) return
        setRows([])
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId, load, reloadKey])

  return (
    <DataTable
      /* A second list on the same screen: the address already belongs to the first. */
      pageParam={null}
      title={title}
      columns={columns}
      rows={rows}
      loading={loading}
      getRowId={getRowId}
      footer={exportType ? <Exports userId={userId} tableType={exportType} /> : undefined}
    />
  )
}

export function ActivitiesTab({ userId, load }) {
  const columns = useMemo(
    () => [
      { key: 'name', header: S.activities.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'date',
        header: S.activities.date,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {asCell(row.date)}
          </span>
        ),
      },
      { key: 'details', header: S.activities.details, render: (row) => <bdi>{row.details}</bdi> },
    ],
    [],
  )

  return (
    <TableTab
      userId={userId}
      load={load}
      columns={columns}
      title={S.tabs.activities}
      exportType="UserActivities"
      getRowId={(_row, index) => index}
    />
  )
}

export function StatusLogTab({ userId, load }) {
  const columns = useMemo(
    () => [
      {
        key: 'status',
        header: S.statusLogs.status,
        value: (row) => S.statusLogs.values[row.status] ?? '',
        render: (row) => (
          <StatusBadge tone={row.status === USER_STATUS.ACTIVE ? 'success' : 'dark'}>
            {S.statusLogs.values[row.status] ?? ''}
          </StatusBadge>
        ),
      },
      {
        key: 'date',
        header: S.statusLogs.date,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {asCell(row.date)}
          </span>
        ),
      },
      { key: 'notes', header: S.statusLogs.notes, render: (row) => <bdi>{row.notes}</bdi> },
    ],
    [],
  )

  return (
    <TableTab
      userId={userId}
      load={load}
      columns={columns}
      title={S.tabs.statusLogs}
      getRowId={(_row, index) => index}
    />
  )
}

export function PoliciesTab({ userId, load }) {
  const columns = useMemo(
    () => [
      { key: 'title', header: S.policies.title, render: (row) => <bdi>{row.title}</bdi> },
      {
        key: 'isDownloaded',
        header: S.policies.isDownloaded,
        value: (row) => (row.isDownloaded ? S.policies.yes : S.policies.no),
        render: (row) => (
          <StatusBadge tone={row.isDownloaded ? 'success' : 'dark'}>
            {row.isDownloaded ? S.policies.yes : S.policies.no}
          </StatusBadge>
        ),
      },
      {
        key: 'date',
        header: S.policies.downloadDate,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {asCell(row.date)}
          </span>
        ),
      },
      {
        key: 'accepted',
        header: S.policies.status,
        value: (row) => (row.accepted ? S.policies.accepted : S.policies.notAccepted),
        render: (row) => (
          <StatusBadge tone={row.accepted ? 'success' : 'warning'}>
            {row.accepted ? S.policies.accepted : S.policies.notAccepted}
          </StatusBadge>
        ),
      },
      {
        key: 'statusDate',
        header: S.policies.statusDate,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {asCell(row.statusDate)}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <TableTab
      userId={userId}
      load={load}
      columns={columns}
      title={S.tabs.policies}
      exportType="UserPoliciesDetails"
      getRowId={(_row, index) => index}
    />
  )
}

const REPORT_TONE = { 0: 'primary', 1: 'warning', 2: 'success', 3: 'dark' }

export function SecurityReportsTab({ userId, load }) {
  const columns = useMemo(
    () => [
      {
        key: 'index',
        header: S.securityReports.index,
        width: '64px',
        sortable: false,
        searchable: false,
        render: (_row, index) => <span className="ud__index">{index + 1}</span>,
      },
      { key: 'id', header: S.securityReports.id },
      { key: 'title', header: S.securityReports.title, render: (row) => <bdi>{row.title}</bdi> },
      { key: 'category', header: S.securityReports.category, render: (row) => <bdi>{row.category}</bdi> },
      {
        key: 'date',
        header: S.securityReports.createdOn,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {asCell(row.date)}
          </span>
        ),
      },
      /* The "last update" column is gone: `SecurityReportDto` has a creation date and
         nothing else, so it could only ever have been blank. */
      {
        key: 'state',
        header: S.securityReports.state,
        /* The server sends the state already localized, so it is shown as received
           rather than mapped through a table of ids that never matched it (W6). */
        value: (row) => row.state ?? '',
        render: (row) => <StatusBadge tone={REPORT_TONE[row.state] ?? 'dark'}>{row.state}</StatusBadge>,
      },
    ],
    [],
  )

  return (
    <TableTab
      userId={userId}
      load={load}
      columns={columns}
      title={S.tabs.securityReports}
      getRowId={(row) => row.id}
    />
  )
}

export function ExamTriesTab({ userId, load, onReset, reloadKey }) {
  const columns = useMemo(
    () => [
      { key: 'name', header: S.examTries.exam, render: (row) => <bdi>{row.name}</bdi> },
      { key: 'allowed', header: S.examTries.allowed },
      { key: 'used', header: S.examTries.used },
      {
        key: 'reset',
        header: S.examTries.reset,
        align: 'center',
        sortable: false,
        searchable: false,
        render: (row) => (
          <Button onClick={() => onReset(row)}>{S.examTries.reset}</Button>
        ),
      },
    ],
    [onReset],
  )

  return (
    <TableTab
      userId={userId}
      load={load}
      reloadKey={reloadKey}
      columns={columns}
      title={S.tabs.examTries}
      getRowId={(row) => row.examId}
    />
  )
}

export function PointsLogTab({ userId, load }) {
  const columns = useMemo(
    () => [
      {
        key: 'activity',
        header: S.pointsLog.activity,
        render: (row) => (
          <span className="ud__activity">
            <bdi>{row.activity}</bdi>
            {row.description && (
              <span className="ud__activity-note">
                <bdi>{row.description}</bdi>
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'date',
        header: S.pointsLog.date,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {asCell(row.date)}
          </span>
        ),
      },
      { key: 'points', header: S.pointsLog.points },
      { key: 'percentageOnDuplication', header: S.pointsLog.percentage },
      {
        key: 'isPositive',
        header: S.pointsLog.type,
        align: 'center',
        render: (row) =>
          row.isPositive ? (
            <ThumbsUp className="ud__icon ud__icon--good" size={18} strokeWidth={1.75} />
          ) : (
            <ThumbsDown className="ud__icon ud__icon--bad" size={18} strokeWidth={1.75} />
          ),
      },
      {
        key: 'effectOpposite',
        header: S.pointsLog.opposite,
        align: 'center',
        render: (row) =>
          row.effectOpposite ? (
            <CircleCheck className="ud__icon ud__icon--good" size={18} strokeWidth={1.75} />
          ) : (
            <CircleX className="ud__icon" size={18} strokeWidth={1.75} />
          ),
      },
    ],
    [],
  )

  return (
    <TableTab
      userId={userId}
      load={load}
      columns={columns}
      title={S.tabs.pointsLog}
      getRowId={(_row, index) => index}
    />
  )
}

/* -------------------------------------------------------------- تقرير الإصطياد --
   A user's side of every campaign that targeted them, and the trail each one
   left. The legacy renders the trail as a timeline in a modal; the trail is the
   whole tab — a status column says the user clicked, the timeline says when,
   from where and on what. */

/** The further along the chain, the worse it went — until 5, which is the win. */
const PHISHING_TONE = { 0: 'dark', 1: 'primary', 2: 'info', 3: 'warning', 4: 'danger', 5: 'success' }

export function PhishingTab({ userId, load }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    load(userId).then((response) => {
      if (cancelled) return
      setRows(response.rows)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [userId, load])

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: S.phishing.campaign,
        /* One name on the DTO, not two: the second line showed a template that
           `PhishingCampaignDto` does not carry. */
        render: (row) => <bdi>{row.name}</bdi>,
      },
      {
        key: 'status',
        header: S.phishing.status,
        // Localized server-side (sent / opened / clicked / submitted / reported).
        value: (row) => row.status ?? '',
        render: (row) => (
          <StatusBadge tone={PHISHING_TONE[row.status] ?? 'dark'}>
            {row.status}
          </StatusBadge>
        ),
      },
      {
        key: 'reportedOn',
        header: S.phishing.reportingDate,
        /* Blank, not a dash: most users never reported, and a column of dashes
           reads as missing data rather than as "this did not happen". */
        render: (row) =>
          row.reportedOn ? (
            <span className="ud__date" dir="ltr">
              {asCell(row.reportedOn)}
            </span>
          ) : null,
      },
      {
        key: 'details',
        header: S.phishing.details,
        align: 'center',
        width: '96px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <IconButton icon={Eye} label={S.phishing.details} tone="info" onClick={() => setOpen(row)} />
        ),
      },
    ],
    [],
  )

  return (
    <>
      <DataTable
        /* A second list on the same screen: the address already belongs to the first. */
        pageParam={null}
        title={S.tabs.phishing}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row) => row.id}
        onRowClick={setOpen}
      />

      <Drawer
        dialog
        open={!!open}
        size="md"
        icon={Fish}
        title={S.phishing.timeline}
        subtitle={open?.name}
        onClose={() => setOpen(null)}
      >
        <ol className="ud__timeline">
          {/* GUARDED, because there is nothing to iterate. `GetPhishingData` answers
              `{CampaignId, Name, Status, ReportingDate}` and no event trail, so this threw
              `Cannot read properties of undefined` on the first click of the eye. The trail
              does exist — `/api/admin/phishing-report/filter` returns `Events[]` per user —
              but it is keyed by campaign and belongs to that screen. */}
          {(open?.events ?? []).map((event) => (
            <li key={`${event.status}-${event.date}`} className={`ud__event is-${PHISHING_TONE[event.status]}`}>
              <span className="ud__event-dot" aria-hidden="true" />
              <div className="ud__event-body">
                <span className="ud__date" dir="ltr">
                  {asCell(event.date)}
                </span>
                <b>
                  <bdi>{S.phishing.states[event.status]}</bdi>
                </b>
                {/* The two fields the legacy hides behind a popover. They are
                    the evidence — a click from an address the user does not
                    own is a different incident — so they are on the event. */}
                <dl className="ud__event-meta" dir="ltr">
                  <div>
                    <dt>IP Address</dt>
                    <dd>{event.ip}</dd>
                  </div>
                  <div>
                    <dt>User Agent</dt>
                    <dd>{event.agent}</dd>
                  </div>
                </dl>
              </div>
            </li>
          ))}
        </ol>
      </Drawer>
    </>
  )
}

/* ------------------------------------------------------------------ القياسات --
   Sorted not-yet-taken first, as the legacy does — the rows that need chasing
   are the ones an admin opened this tab for. */

export function AssessmentsTab({ userId, load }) {
  const columns = useMemo(
    () => [
      { key: 'index', header: S.assessments.index, width: '64px', searchable: false },
      { key: 'title', header: S.assessments.title, render: (row) => <bdi>{row.title}</bdi> },
      /* Start and end dates are gone — `AssessmentDto` carries neither. What it does
         carry, and what the screen was not showing, is the SCORE and the completion
         date; both are more use than an empty column. */
      {
        key: 'status',
        header: S.assessments.state,
        value: (row) => row.status ?? '',
        render: (row) => (
          <StatusBadge tone={row.completed ? 'success' : 'warning'}>{row.status}</StatusBadge>
        ),
      },
      { key: 'score', header: S.assessments.score, align: 'center' },
      {
        key: 'completed',
        header: S.assessments.completedOn,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {asCell(row.completed)}
          </span>
        ),
      },
      {
        key: 'mandatory',
        header: S.assessments.mandatory,
        value: (row) => row.mandatory ?? '',
        render: (row) => (
          <StatusBadge tone="dark" plain>
            {row.mandatory}
          </StatusBadge>
        ),
      },
    ],
    [],
  )

  return (
    <TableTab
      userId={userId}
      load={load}
      columns={columns}
      title={S.tabs.assessments}
      getRowId={(row) => row.id}
    />
  )
}

/* --------------------------------------------------------------- الاستبيانات --
   `تحميل التفاصيل` only exists on a survey the user answered. The legacy still
   renders the button on the others, greyed with a tooltip; here the cell says
   why instead, because a button that cannot be pressed is not an explanation. */

export function SurveysTab({ userId, load, onDownload }) {
  const columns = useMemo(
    () => [
      { key: 'index', header: S.surveys.index, width: '64px', searchable: false },
      { key: 'title', header: S.surveys.title, render: (row) => <bdi>{row.title}</bdi> },
      {
        key: 'createdOn',
        header: S.surveys.createdOn,
        render: (row) => <Day iso={row.createdOn} />,
      },
      {
        key: 'mandatory',
        header: S.surveys.mandatory,
        value: (row) => row.mandatory ?? '',
        render: (row) => (
          <StatusBadge tone="dark" plain>
            {row.mandatory}
          </StatusBadge>
        ),
      },
      {
        key: 'answered',
        header: S.surveys.state,
        value: (row) => (row.answered ? S.surveys.answered : S.surveys.notAnswered),
        render: (row) => (
          <StatusBadge tone={row.answered ? 'success' : 'dark'}>
            {row.answered ? S.surveys.answered : S.surveys.notAnswered}
          </StatusBadge>
        ),
      },
      {
        key: 'download',
        header: S.surveys.download,
        align: 'center',
        width: '150px',
        sortable: false,
        searchable: false,
        render: (row) =>
          row.answered ? (
            <IconButton
              icon={Download}
              label={S.surveys.download}
              tone="success"
              onClick={() => onDownload(row)}
            />
          ) : (
            /* A dash, not the sentence. `الحالة` beside it already says the
               survey went unanswered, and the sentence wrapped every such row
               to three lines to repeat it. */
            <span className="ud__muted" title={S.surveys.notAnsweredHint}>
              —
            </span>
          ),
      },
    ],
    [onDownload],
  )

  return (
    <TableTab
      userId={userId}
      load={load}
      columns={columns}
      title={S.tabs.surveys}
      getRowId={(row) => row.id}
    />
  )
}

/** A date is a Latin run whatever the page direction is. */
function Day({ iso }) {
  return (
    <span className="ud__date" dir="ltr">
      {asDay(iso)}
    </span>
  )
}
